import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="notification-dropdown"
export default class extends Controller {
  static targets = ["button", "menu", "template"];

  connect() {
    // Clean up any existing dropdown popups when the page loads
    this.clearAllDropdowns();

    // Make button text smaller on mobile but maintain height with extra padding
    const isMobile = window.innerWidth < 640; // sm breakpoint
    if (isMobile && this.hasButtonTarget) {
      const button = this.buttonTarget;
      button.classList.remove("text-sm");
      button.classList.add("text-xs");
      // Add extra padding to maintain same height as Watchlist button
      button.classList.remove("py-2");
      button.classList.add("py-3");
    }

    // Add event listener for clicks outside the dropdown
    document.addEventListener("click", this.handleOutsideClick.bind(this));
  }

  disconnect() {
    // Remove scroll listeners and dropdowns when controller disconnects
    window.removeEventListener("scroll", this.scrollHandler);
    document.removeEventListener("click", this.handleOutsideClick.bind(this));
    this.clearAllDropdowns();
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    const buttonId = event.currentTarget.id.split("-").pop();

    // Check if dropdown is already open
    const isExpanded =
      event.currentTarget.getAttribute("aria-expanded") === "true";

    // Close all dropdowns
    this.clearAllDropdowns();

    // If it was already open, we're done (toggle off)
    if (isExpanded) {
      return;
    }

    // Create new dropdown element
    const templateId = `dropdown-content-${buttonId}`;
    const template = document.getElementById(templateId);

    if (!template) return;

    // Clone the template content
    const content = template.content.cloneNode(true);

    // Create a container for the dropdown
    const dropdownMenu = document.createElement("div");
    dropdownMenu.className = "notification-dropdown-menu";
    dropdownMenu.setAttribute("role", "menu");
    dropdownMenu.setAttribute("aria-labelledby", event.currentTarget.id);
    dropdownMenu.setAttribute("data-button-id", buttonId);

    // Style the dropdown
    dropdownMenu.style.position = "absolute";
    dropdownMenu.style.zIndex = "99999";
    dropdownMenu.style.boxShadow =
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
    dropdownMenu.style.border = "1px solid rgba(0, 0, 0, 0.1)";
    dropdownMenu.style.backgroundColor = "#1e293b";
    dropdownMenu.style.borderRadius = "0.375rem";

    // Add content to dropdown
    dropdownMenu.appendChild(content);

    // Make text smaller on mobile devices
    const isMobile = window.innerWidth < 640; // sm breakpoint
    if (isMobile) {
      // Target notification option labels and make them smaller
      const notificationLabels = dropdownMenu.querySelectorAll(
        'label[for*="notification_type_"]'
      );
      notificationLabels.forEach((label) => {
        label.classList.remove("text-sm");
        label.classList.add("text-xs");
        // Also reduce padding slightly on mobile
        label.classList.remove("py-3");
        label.classList.add("py-2");
      });

      // Target remove from watchlist button and make it smaller
      const removeButton = dropdownMenu.querySelector(
        'button[data-action*="removeFromWatchlist"]'
      );
      if (removeButton) {
        removeButton.classList.remove("text-sm");
        removeButton.classList.add("text-xs");
        // Also reduce padding slightly on mobile
        removeButton.classList.remove("py-3");
        removeButton.classList.add("py-2");
      }
    }

    // Add event listeners to cloned content since it's outside the controller scope
    const removeButton = dropdownMenu.querySelector(
      '[data-action*="removeFromWatchlist"]'
    );
    if (removeButton) {
      removeButton.addEventListener(
        "click",
        this.removeFromWatchlist.bind(this)
      );
    }

    // Append to body
    document.body.appendChild(dropdownMenu);

    // Position the dropdown
    this.positionDropdown(dropdownMenu, event.currentTarget);

    // Add scroll event listener to update position
    this.scrollHandler = this.handleScroll.bind(this, buttonId, dropdownMenu);
    window.addEventListener("scroll", this.scrollHandler);

    // Mark button as expanded
    event.currentTarget.setAttribute("aria-expanded", "true");
  }

  // Clear all open dropdowns
  clearAllDropdowns() {
    document
      .querySelectorAll(".notification-dropdown-menu")
      .forEach(function (el) {
        el.remove();
      });

    // Reset all buttons
    document
      .querySelectorAll('[id^="dropdown-button-"]')
      .forEach(function (button) {
        button.setAttribute("aria-expanded", "false");
      });
  }

  // Handle outside clicks to close dropdowns
  handleOutsideClick(event) {
    if (
      !event.target.closest('[id^="dropdown-button-"]') &&
      !event.target.closest(".notification-dropdown-menu")
    ) {
      this.clearAllDropdowns();
    }
  }

  // Handle form submit completion
  handleSubmit(event) {
    // Close the dropdown after successful submission
    this.clearAllDropdowns();
  }

  // Handle remove from watchlist action
  removeFromWatchlist(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const url = button.dataset.url;
    const params = JSON.parse(button.dataset.params || "{}");
    const mediaTitle = button.dataset.mediaTitle;

    // Create form data
    const formData = new FormData();
    formData.append("_method", "delete");

    // Add CSRF token
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    if (csrfToken) {
      formData.append("authenticity_token", csrfToken);
    }

    // Add params
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    // Make the request using fetch with Turbo Stream headers
    fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        throw new Error(
          `Network response was not ok: ${response.status} ${response.statusText}`
        );
      })
      .then((html) => {
        // Execute the Turbo Stream response
        if (html.includes("<turbo-stream")) {
          document.dispatchEvent(
            new CustomEvent("turbo:before-stream-render", { detail: { html } })
          );

          // Create a temporary element to parse the Turbo Stream
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          // Execute each turbo-stream element
          tempDiv.querySelectorAll("turbo-stream").forEach((stream) => {
            const action = stream.getAttribute("action");
            const target = stream.getAttribute("target");
            const targets = stream.getAttribute("targets"); // For replace_all actions
            const template = stream.querySelector("template");

            if (action === "replace" && target && template) {
              const targetElement = document.getElementById(target);
              if (targetElement) {
                targetElement.outerHTML = template.innerHTML;
              }
            } else if (action === "replace" && targets && template) {
              // Handle replace with targets attribute (replace_all from Rails)
              const targetElements = document.querySelectorAll(targets);
              targetElements.forEach((element) => {
                element.outerHTML = template.innerHTML;
              });
            } else if (action === "prepend" && target && template) {
              const targetElement = document.getElementById(target);
              if (targetElement) {
                targetElement.insertAdjacentHTML(
                  "afterbegin",
                  template.innerHTML
                );
              }
            }
          });
        }

        // Close the dropdown
        this.clearAllDropdowns();
      })
      .catch((error) => {
        console.error("Error removing from watchlist:", error);
        // Still close the dropdown on error
        this.clearAllDropdowns();
      });
  }

  // Handle scroll updates
  handleScroll(buttonId, dropdownMenu) {
    const button = document.getElementById(`dropdown-button-${buttonId}`);
    if (
      button &&
      document.querySelector(
        `.notification-dropdown-menu[data-button-id="${buttonId}"]`
      )
    ) {
      this.positionDropdown(dropdownMenu, button);
    } else {
      // Button or dropdown no longer exists, remove scroll listener
      window.removeEventListener("scroll", this.scrollHandler);
    }
  }

  // Position dropdown correctly
  positionDropdown(dropdown, button) {
    if (!button || !dropdown) return;

    const buttonRect = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 640; // sm breakpoint

    // Make dropdown width responsive - smaller on mobile
    const minDropdownWidth = isMobile ? 200 : 280; // Reduced from 280 to 200 on mobile
    const dropdownWidth = Math.max(minDropdownWidth, buttonRect.width);

    // Position dropdown at the exact coordinates of the button + scroll position
    dropdown.style.width = dropdownWidth + "px";
    dropdown.style.top = window.scrollY + buttonRect.bottom + 5 + "px";

    // Check if dropdown would overflow on the right edge
    const wouldOverflowRight = buttonRect.left + dropdownWidth > viewportWidth;

    // On mobile, be more aggressive about right-positioning dropdowns
    let shouldPositionRight = false;
    let shouldPositionCenter = false;

    if (isMobile) {
      // On mobile with 3-column grid, divide viewport into thirds
      // Use button center position for more accurate column detection
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const leftThreshold = viewportWidth * 0.33; // First 33%
      const rightThreshold = viewportWidth * 0.67; // Last 33%

      if (buttonCenter <= leftThreshold) {
        // Left column - use left alignment
        shouldPositionRight = false;
        shouldPositionCenter = false;
        console.log("Positioning: LEFT");
      } else if (buttonCenter >= rightThreshold) {
        // Right column - use right alignment
        shouldPositionRight = true;
        shouldPositionCenter = false;
        console.log("Positioning: RIGHT");
      } else {
        // Center column - use center alignment (bounds checking will handle overflow)
        shouldPositionRight = false;
        shouldPositionCenter = true;
        console.log("Positioning: CENTER");
      }
    } else {
      // Desktop behavior - only position right if truly at edge with buffer
      const edgeBuffer = 20; // 20px buffer from viewport edge
      const isNearRightEdge = buttonRect.right > viewportWidth - edgeBuffer;
      shouldPositionRight = wouldOverflowRight && isNearRightEdge;
    }

    if (shouldPositionCenter) {
      // Center align dropdown with the button
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      let centeredLeft = window.scrollX + buttonCenter - dropdownWidth / 2;

      // Ensure dropdown doesn't go off the left edge of viewport
      const minLeft = window.scrollX + 10; // 10px padding from left edge
      centeredLeft = Math.max(centeredLeft, minLeft);

      // Ensure dropdown doesn't go off the right edge either
      const maxLeft = window.scrollX + viewportWidth - dropdownWidth - 10; // 10px padding from right edge
      centeredLeft = Math.min(centeredLeft, maxLeft);

      dropdown.style.left = centeredLeft + "px";
    } else if (shouldPositionRight) {
      // Position dropdown to align with the right edge of the button
      dropdown.style.left =
        window.scrollX + buttonRect.right - dropdownWidth + "px";
    } else {
      // Normal positioning - align with left edge of button
      dropdown.style.left = window.scrollX + buttonRect.left + "px";
    }

    // Check if dropdown is visible within viewport vertically, if not, position it above the button
    const dropdownHeight = dropdown.offsetHeight;
    const viewportHeight = window.innerHeight;
    const bottomSpace = viewportHeight - buttonRect.bottom;

    if (dropdownHeight > bottomSpace && buttonRect.top > dropdownHeight) {
      // Position above button if there's more space above
      dropdown.style.top =
        window.scrollY + buttonRect.top - dropdownHeight - 5 + "px";
    }
  }
}
